<!-- 
  DEPENDENCY GRAPH VISUALIZATION
  
  Interactive workflow dependency graph using ngx-graph library.
  Features: zoom, pan, node selection, connection editing, cycle detection.
  Layout: DAG (Directed Acyclic Graph) using Dagre algorithm.
-->
<div class="graph-section">
  <!-- Graph header with Clear All button -->
  <div class="graph-header" *ngIf="graphConfigurations.length > 0 && isFormEditable">
    <button mat-stroked-button 
            color="warn" 
            (click)="onClearAll()"
            matTooltip="Clear all configurations"
            fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="4px">
      <mat-icon>clear_all</mat-icon>
      Clear All
    </button>
  </div>
  
  <!-- No configurations message -->
  <div *ngIf="graphConfigurations.length === 0" fxLayout="column" fxLayoutAlign="start center" class="no-configurations-message">
    <mat-icon>account_tree</mat-icon>
    <div class="message-title">No configurations added to graph.</div>
  </div>
  
  <!-- Graph visualization -->
  <div *ngIf="graphConfigurations.length > 0" class="graph-visualization-container">
    <div class="graph-container">
      <ngx-graph
        class="chart-container"
        [nodes]="nodes"
        [links]="links"
        [enableZoom]="graphConfig.enableZoom"
        [panOnZoom]="graphConfig.panOnZoom"
        [zoomSpeed]="graphConfig.zoomSpeed"
        [minZoomLevel]="graphConfig.minZoomLevel"
        [maxZoomLevel]="graphConfig.maxZoomLevel"
        [layout]="graphConfig.layout"
        [layoutSettings]="graphConfig.layoutSettings"
        [draggingEnabled]="draggingEnabled"
        [panningEnabled]="panningEnabled"
        [autoZoom]="autoZoom"
        [autoCenter]="autoCenter"
        [showMiniMap]="showMiniMap"
        (select)="onNodeSelect($event)">

        <!-- Arrow marker definitions for edge endpoints -->
        <ng-template #defsTemplate>
          <!-- Default gray arrow -->
          <svg:marker id="arrow" viewBox="0 -5 10 10" refX="8" refY="0" markerWidth="4" markerHeight="4" orient="auto">
            <svg:path d="M0,-5L10,0L0,5" class="arrow-head" />
          </svg:marker>
        </ng-template>

        <!-- 
          Custom Node Template
          Displays workflow configuration as rounded rectangle with delete button
          Width adjusts dynamically based on label length
          Red border indicates blocking node, blue border for non-blocking
          Tooltip shows blocking behavior on hover
        -->
        <ng-template #nodeTemplate let-node>
          <svg:g class="node">
            <!-- Tooltip for node (native SVG title) -->
            <svg:title>{{ getNodeTooltip(node.id) }}</svg:title>
            
            <!-- Node background rectangle -->
            <svg:rect
              [attr.width]="getNodeWidth(node.label)"
              [attr.height]="node.dimension?.height || 40"
              fill="#fff"
              [attr.stroke]="getNodeStrokeColor(node.id)"
              [attr.stroke-opacity]="getNodeOpacity(node.id)"
              [attr.stroke-width]="2"
              [attr.rx]="8" 
              [attr.ry]="8">
            </svg:rect>
            
            <!-- Node label text -->
            <svg:text 
              alignment-baseline="central" 
              [attr.x]="10" 
              [attr.y]="(node.dimension?.height || 40) / 2"
              fill="#333"
              [attr.fill-opacity]="getNodeOpacity(node.id)"
              [attr.font-size]="'12px'"
              [attr.font-weight]="'500'"
              [attr.text-anchor]="'start'">
              {{node.label}}
            </svg:text>
            
            <!-- Delete button (X) in top-right corner - shows on hover (only in edit mode) -->
            <svg:g
              *ngIf="isFormEditable"
              class="node-delete"
              [attr.transform]="'translate(' + (getNodeWidth(node.label) - 16) + ', 2)'"
              (click)="onDeleteNode(node.id, $event)">
              <svg:rect width="14" height="14" rx="3" ry="3" class="node-delete-bg"></svg:rect>
              <svg:path d="M3 3 L11 11 M11 3 L3 11" class="node-delete-x"></svg:path>
            </svg:g>
          </svg:g>
        </ng-template>

        <!-- 
          Custom Link/Edge Template
          Displays connection between nodes with arrow marker
          Shows label at midpoint of connection line
        -->
        <ng-template #linkTemplate let-link>
          <svg:g class="edge">
            <svg:path
              class="line"
              [attr.stroke]="getEdgeStrokeColor(link)"
              [attr.opacity]="getEdgeOpacity(link)"
              stroke-width="2"
              [attr.id]="link.id"
              [attr.d]="link.line"
              marker-end="url(#arrow)"
              fill="none">
            </svg:path>
            <svg:text class="edge-label" text-anchor="middle" fill="#666" [attr.fill-opacity]="getEdgeOpacity(link)" [attr.font-size]="'10px'">
              <textPath
                class="text-path"
                [attr.href]="'#' + link.id"
                [style.dominant-baseline]="link.dominantBaseline"
                startOffset="50%">
                {{link.label}}
              </textPath>
            </svg:text>
          </svg:g>
        </ng-template>

      </ngx-graph>
    </div>
    
    <!-- Legend for node colors -->
    <div class="graph-legend" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="16px">
      <div class="legend-item" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="6px"
           [matTooltip]="blockingLegendTooltip" matTooltipPosition="above">
        <div class="legend-box blocking"></div>
        <span class="legend-label">Blocking</span>
      </div>
      <div class="legend-item" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="6px"
           [matTooltip]="nonBlockingLegendTooltip" matTooltipPosition="above">
        <div class="legend-box non-blocking"></div>
        <span class="legend-label">Non-Blocking</span>
      </div>
      <!-- TODO: enable dag node coloring -->
      <!-- <div class="legend-item" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="6px"
           [matTooltip]="deletedLegendTooltip" matTooltipPosition="above">
        <div class="legend-box deleted"></div>
        <span class="legend-label">Deleted</span>
      </div> -->
      <!-- TODO: enable dag node coloring -->
      <!-- <div class="legend-item" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="6px"
           [matTooltip]="inactiveLegendTooltip" matTooltipPosition="above">
        <div class="legend-box inactive"></div>
        <span class="legend-label">Inactive</span>
      </div> -->
    </div>
  </div>
</div>

<!-- 
  Edit Targets Popup Dialog
  Allows user to modify which nodes a configuration connects to
  Validates for cycles before saving changes
-->
<div *ngIf="showEditTargetsPopup" fxLayout="row" fxLayoutAlign="center center" class="popup-overlay" (click)="closePopupFromOverlay()">
  <div class="popup-content" (click)="$event.stopPropagation()">
    <!-- Dialog header with title and close button -->
    <div fxLayout="row" fxLayoutAlign="space-between center" class="popup-header">
      <div class="popup-title">Edit Connections</div>
      <button mat-icon-button (click)="closeEditTargetsPopup()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    
    <!-- Dialog body with instruction and target selection -->
    <div class="popup-body">
      <!-- isBlocking toggle at top right -->
      <div fxLayout="row" fxLayoutAlign="space-between center" class="blocking-toggle-row">
        <p class="popup-instruction">
          Edit target connections for <strong>{{ selectedSourceNodeForEdit?.name }}</strong>:
        </p>
        <div class="blocking-toggle-container" fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="4px">
          <span class="blocking-label">Is Blocking</span>
          <span [matTooltip]="selectedTargetConfigIds.length === 0 ? nonBlockingDisabledTooltip : isBlockingTooltip">
            <mat-slide-toggle 
              [(ngModel)]="isBlocking"
              name="isBlockingToggle"
              color="primary"
              class="blocking-toggle"
              [disabled]="selectedTargetConfigIds.length === 0">
            </mat-slide-toggle>
          </span>
        </div>
      </div>
      
      <!-- Multi-select dropdown for choosing target nodes -->
      <mat-form-field appearance="outline" class="source-configs-dropdown">
        <mat-label>Select Target Configuration(s)</mat-label>
        <mat-select [(value)]="selectedTargetConfigIds" multiple>
          <mat-option *ngFor="let config of availableTargetConfigs" [value]="config.id">
            {{ config.name }}
          </mat-option>
        </mat-select>
        <mat-hint>Select nodes to connect to</mat-hint>
      </mat-form-field>
      
      <!-- Error message (e.g., cycle detection) -->
      <div *ngIf="editPopupError" class="popup-error">
        {{ editPopupError }}
      </div>
    </div>
    
    <!-- Dialog actions: Cancel and Save buttons -->
    <div fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="12px" class="popup-actions">
      <button mat-stroked-button (click)="closeEditTargetsPopup()">
        Cancel
      </button>
      <button mat-raised-button color="primary" 
              (click)="saveUpdatedTargets()">
        Save Changes
      </button>
    </div>
  </div>
</div>
